import React, { useEffect, useMemo, useState } from "react"
import { apiFetch } from "../../../utils/apiFetch"
import { ui } from "../../../shared/styles/uiSystem"

const DEFAULT_DURATION_OPTIONS = [15, 30, 45, 60, 90, 120, 150, 180]

export default function EditAppointmentModal({
  open,
  appointment,
  clinicId,
  treatments = [],
  doctors = [],
  chairs = [],
  saving,
  isReadOnly,
  readOnlyReason,
  onClose,
  onSave,
}) {
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1440,
  )
  const [form, setForm] = useState(() => buildInitialForm({ appointment, treatments, doctors, chairs }))
  const [slotsPayload, setSlotsPayload] = useState(null)
  const [loadingSlots, setLoadingSlots] = useState(false)
  const [slotError, setSlotError] = useState("")

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth)
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }, [])

  useEffect(() => {
    if (!open) return
    setForm(buildInitialForm({ appointment, treatments, doctors, chairs }))
    setSlotsPayload(null)
    setSlotError("")
  }, [open, appointment, treatments, doctors, chairs])

  useEffect(() => {
    if (!open) return
    if (!clinicId || clinicId === "ALL") return
    if (!form.date || !form.treatmentId) {
      setSlotsPayload(null)
      setSlotError("")
      return
    }

    let cancelled = false

    const loadSlots = async () => {
      try {
        setLoadingSlots(true)
        setSlotError("")

        const response = await apiFetch("/api/appointments/available-slots", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            clinicId,
            date: form.date,
            treatmentId: form.treatmentId,
            durationMinutes: Number(form.durationMinutes || 30),
            doctorId: form.doctorId || undefined,
            chairId: form.chairId || undefined,
            excludeAppointmentId: appointment?.id || undefined,
          }),
        })

        const data = await response.json().catch(() => null)

        if (cancelled) return

        if (!response.ok) {
          const message = data?.message || "No se pudieron cargar horarios disponibles"
          setSlotError(Array.isArray(message) ? message[0] : message)
          setSlotsPayload(null)
          return
        }

        setSlotsPayload(data || null)

        if (data?.selectedDoctor?.id && !form.doctorId) {
          setForm((prev) => ({ ...prev, doctorId: prev.doctorId || data.selectedDoctor.id }))
        }
      } catch {
        if (!cancelled) {
          setSlotError("No se pudieron cargar horarios disponibles")
          setSlotsPayload(null)
        }
      } finally {
        if (!cancelled) setLoadingSlots(false)
      }
    }

    loadSlots()

    return () => {
      cancelled = true
    }
  }, [open, clinicId, form.date, form.treatmentId, form.durationMinutes, form.doctorId, form.chairId, appointment?.id])

  const isMobile = viewportWidth < 720
  const safeTreatments = Array.isArray(treatments) ? treatments : []
  const safeDoctors = Array.isArray(doctors) ? doctors : []
  const safeChairs = Array.isArray(chairs) ? chairs : []
  const singleDoctor = safeDoctors.length === 1 ? safeDoctors[0] : null
  const singleChair = safeChairs.length === 1 ? safeChairs[0] : null
  const selectedTreatment = safeTreatments.find((item) => String(item.id) === String(form.treatmentId))
  const currentSlot = buildCurrentSlot(appointment)

  const availableSlots = useMemo(() => {
    const suggested = Array.isArray(slotsPayload?.suggested) ? slotsPayload.suggested : []
    const allSlots = Array.isArray(slotsPayload?.slots) ? slotsPayload.slots : []
    const available = [...suggested, ...allSlots.filter((slot) => slot?.status === "available")]

    const unique = []
    for (const slot of available) {
      if (!slot?.startTime) continue
      if (!unique.some((item) => sameSlotValue(item.startTime, slot.startTime))) unique.push(slot)
    }

    if (!currentSlot || form.date !== currentSlot.date) return unique

    const alreadyIncluded = unique.some((slot) => sameSlotValue(slot.startTime, currentSlot.startTime))
    if (alreadyIncluded) return unique

    return [currentSlot, ...unique]
  }, [slotsPayload, currentSlot, form.date])

  const selectedSlot = availableSlots.find((slot) => sameSlotValue(slot.startTime, form.slotStartTime)) || null
  const suggestedSlots = availableSlots
  const visibleScheduleWarning = normalizeScheduleWarning(slotsPayload?.scheduleWarning)
  const canSave = Boolean(form.date && form.treatmentId && form.slotStartTime && !saving && !isReadOnly)

  if (!open || !appointment) return null

  const setField = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value }

      if (["date", "treatmentId", "durationMinutes", "doctorId", "chairId"].includes(field)) {
        next.slotStartTime = ""
      }

      if (field === "treatmentId") {
        const treatment = safeTreatments.find((item) => String(item.id) === String(value))
        if (treatment?.durationMinutes) {
          next.durationMinutes = Number(treatment.durationMinutes || next.durationMinutes || 30)
        }
      }

      return next
    })
  }

  const applySlot = (slot) => {
    if (!slot || slot.status !== "available") return

    setForm((prev) => ({
      ...prev,
      slotStartTime: slot.startTime,
      doctorId: prev.doctorId || slot.doctorId || prev.doctorId,
      chairId: prev.chairId || slot.chairId || prev.chairId,
    }))
  }

  const handleSubmit = async (event) => {
    event.preventDefault()

    if (isReadOnly) {
      alert(readOnlyReason || "Modo solo lectura activo")
      return
    }

    if (!selectedSlot) {
      alert("Selecciona un horario disponible")
      return
    }

    const normalizedSlotRange = buildSlotRangeFromLocalLabel({
      date: form.date,
      slot: selectedSlot,
      durationMinutes: Number(form.durationMinutes || 30),
    })

    const slotStart = normalizedSlotRange.startTime
    const slotEnd = normalizedSlotRange.endTime

    const ok = await onSave?.({
      appointmentId: appointment.id,
      clinicId,
      startTime: slotStart,
      endTime: slotEnd,
      doctorId: form.doctorId || selectedSlot.doctorId || singleDoctor?.id || getAppointmentDoctorId(appointment),
      chairId: form.chairId || singleChair?.id || null,
      treatmentId: form.treatmentId,
      reason: selectedTreatment?.name || getAppointmentTreatmentName(appointment),
      notes: form.notes,
    })

    if (ok) onClose?.()
  }

  return (
    <div style={overlayStyle} onMouseDown={onClose}>
      <form
        onMouseDown={(event) => event.stopPropagation()}
        onSubmit={handleSubmit}
        style={{
          ...modalStyle,
          width: isMobile ? "calc(100vw - 24px)" : 720,
          maxHeight: isMobile ? "calc(100vh - 24px)" : "calc(100vh - 56px)",
        }}
      >
        <div style={headerStyle}>
          <div>
            <div style={eyebrowStyle}>Editar cita</div>
            <h2 style={titleStyle}>Reprogramar cita</h2>
            <p style={subtitleStyle}>
              Ajusta la agenda sin modificar tratamientos registrados, cobros ni bitácora.
            </p>
          </div>
          <button type="button" onClick={onClose} style={closeButtonStyle} aria-label="Cerrar">
            ×
          </button>
        </div>

        {isReadOnly ? <div style={dangerCardStyle}>{readOnlyReason || "Modo solo lectura activo"}</div> : null}

        <div style={topGridStyle(isMobile)}>
          <label style={fieldStyle}>
            <span style={labelStyle}>Tratamiento</span>
            <select
              value={form.treatmentId}
              onChange={(event) => setField("treatmentId", event.target.value)}
              style={inputStyle}
            >
              <option value="">Selecciona tratamiento</option>
              {safeTreatments.map((treatment) => (
                <option key={treatment.id} value={treatment.id}>
                  {treatment.name} · {Number(treatment.durationMinutes || 30)} min
                </option>
              ))}
            </select>
          </label>

          <label style={fieldStyle}>
            <span style={labelStyle}>Duración</span>
            <select
              value={String(form.durationMinutes)}
              onChange={(event) => setField("durationMinutes", Number(event.target.value || 30))}
              style={inputStyle}
            >
              {durationOptions(selectedTreatment).map((minutes) => (
                <option key={minutes} value={minutes}>{minutes} min</option>
              ))}
            </select>
          </label>
        </div>

        <div style={resourceGridStyle(isMobile)}>
          <label style={fieldStyle}>
            <span style={labelStyle}>Fecha</span>
            <input
              type="date"
              value={form.date}
              onChange={(event) => setField("date", event.target.value)}
              style={inputStyle}
            />
          </label>

          <div style={fieldStyle}>
            <span style={labelStyle}>Doctor</span>
            {singleDoctor ? (
              <div style={fixedValueStyle}>{singleDoctor.name || "Doctor único"}</div>
            ) : (
              <select
                value={form.doctorId}
                onChange={(event) => setField("doctorId", event.target.value)}
                style={inputStyle}
              >
                <option value="">Asignación automática</option>
                {safeDoctors.map((doctor) => (
                  <option key={doctor.id} value={doctor.id}>{doctor.name || "Doctor"}</option>
                ))}
              </select>
            )}
          </div>

          <div style={fieldStyle}>
            <span style={labelStyle}>Silla</span>
            {singleChair ? (
              <div style={fixedValueStyle}>{singleChair.name || "Silla única"}</div>
            ) : (
              <select
                value={form.chairId}
                onChange={(event) => setField("chairId", event.target.value)}
                style={inputStyle}
              >
                <option value="">Sin silla específica</option>
                {safeChairs.map((chair) => (
                  <option key={chair.id} value={chair.id}>{chair.name || "Silla"}</option>
                ))}
              </select>
            )}
          </div>
        </div>

        <section style={slotsPanelStyle}>
          <div style={slotsHeaderStyle}>
            <div style={labelStyle}>Horario disponible</div>
            <span style={pillStyle}>Bloques reales</span>
          </div>

          {!form.treatmentId ? (
            <div style={infoCardStyle}>Selecciona un tratamiento para calcular horarios.</div>
          ) : loadingSlots ? (
            <div style={infoCardStyle}>Calculando horarios disponibles...</div>
          ) : slotError ? (
            <div style={dangerCardStyle}>{slotError}</div>
          ) : (
            <>
              {visibleScheduleWarning ? <div style={softWarningStyle}>{visibleScheduleWarning}</div> : null}

              {suggestedSlots.length ? (
                <div style={chipsRowStyle}>
                  {suggestedSlots.map((slot) => (
                    <button
                      key={`chip-${slot.startTime}`}
                      type="button"
                      onClick={() => applySlot(slot)}
                      style={{
                        ...chipStyle,
                        ...(sameSlotValue(form.slotStartTime, slot.startTime) ? selectedChipStyle : null),
                      }}
                    >
                      {formatSlotLabel(slot)}
                    </button>
                  ))}
                </div>
              ) : null}

              <select
                value={form.slotStartTime}
                onChange={(event) => {
                  const slot = availableSlots.find((item) => sameSlotValue(item.startTime, event.target.value))
                  if (slot) applySlot(slot)
                  else setField("slotStartTime", "")
                }}
                style={inputStyle}
              >
                <option value="">Selecciona horario libre</option>
                {availableSlots.map((slot) => (
                  <option key={slot.startTime} value={slot.startTime}>
                    {formatSlotLabel(slot)}{slot.isCurrent ? " · hora actual" : ""}{slot.doctorName ? ` · ${slot.doctorName}` : ""}
                  </option>
                ))}
              </select>

              {!availableSlots.length ? (
                <div style={infoCardStyle}>No hay horarios libres para esta combinación.</div>
              ) : null}
            </>
          )}
        </section>

        <label style={{ ...fieldStyle, marginTop: 12 }}>
          <span style={labelStyle}>Notas internas de la cita</span>
          <textarea
            value={form.notes}
            onChange={(event) => setField("notes", event.target.value)}
            style={{ ...inputStyle, minHeight: 76, resize: "vertical" }}
            placeholder="Notas operativas visibles para recepción o doctor"
          />
        </label>

        <div style={footerStyle}>
          <button type="button" onClick={() => setForm(buildInitialForm({ appointment, treatments, doctors, chairs }))} style={ui.secondaryButton}>
            Restaurar datos
          </button>
          <button type="button" onClick={onClose} style={ui.secondaryButton}>Cancelar</button>
          <button type="submit" disabled={!canSave} style={canSave ? ui.primaryButton : disabledButtonStyle}>
            {saving ? "Guardando..." : form.slotStartTime ? "Guardar cambios" : "Selecciona horario"}
          </button>
        </div>
      </form>
    </div>
  )
}

function buildInitialForm({ appointment, treatments, doctors, chairs }) {
  const currentStart = getAppointmentStart(appointment)
  const currentEnd = getAppointmentEnd(appointment)
  const treatmentId = getAppointmentTreatmentId(appointment) || matchTreatmentByName(treatments, getAppointmentTreatmentName(appointment))
  const selectedTreatment = Array.isArray(treatments)
    ? treatments.find((item) => String(item.id) === String(treatmentId))
    : null

  return {
    date: toDateInputValue(currentStart),
    treatmentId: treatmentId || "",
    durationMinutes: getDurationMinutes(currentStart, currentEnd, selectedTreatment),
    doctorId: getAppointmentDoctorId(appointment) || (doctors?.length === 1 ? doctors[0]?.id || "" : ""),
    chairId: getAppointmentChairId(appointment) || (chairs?.length === 1 ? chairs[0]?.id || "" : ""),
    slotStartTime: currentStart || "",
    notes: appointment?.notes || appointment?.extendedProps?.notes || "",
  }
}

function getAppointmentStart(appointment) {
  return appointment?.startTime || appointment?.start || appointment?.extendedProps?.startTime || ""
}

function getAppointmentEnd(appointment) {
  return appointment?.endTime || appointment?.end || appointment?.extendedProps?.endTime || ""
}

function getAppointmentDoctorId(appointment) {
  return appointment?.doctorId || appointment?.extendedProps?.doctorId || appointment?.doctor?.id || ""
}

function getAppointmentChairId(appointment) {
  return appointment?.chairId || appointment?.extendedProps?.chairId || appointment?.chair?.id || ""
}

function getFirstTreatmentLike(appointment) {
  const sources = [
    appointment?.treatmentLogs,
    appointment?.appointmentTreatments,
    appointment?.treatments,
    appointment?.extendedProps?.treatmentLogs,
    appointment?.extendedProps?.appointmentTreatments,
    appointment?.extendedProps?.treatments,
  ]

  for (const source of sources) {
    if (Array.isArray(source) && source.length) return source[0]
  }

  return null
}

function getAppointmentTreatmentId(appointment) {
  const first = getFirstTreatmentLike(appointment)

  return (
    appointment?.treatmentId ||
    appointment?.extendedProps?.treatmentId ||
    appointment?.treatment?.id ||
    first?.treatmentId ||
    first?.treatment?.id ||
    first?.id ||
    ""
  )
}

function getAppointmentTreatmentName(appointment) {
  const first = getFirstTreatmentLike(appointment)

  return (
    appointment?.treatmentName ||
    appointment?.reason ||
    appointment?.extendedProps?.treatmentName ||
    appointment?.extendedProps?.reason ||
    appointment?.treatment?.name ||
    first?.treatment?.name ||
    first?.name ||
    ""
  )
}

function matchTreatmentByName(treatments, name) {
  const cleanName = normalizeText(name)
  if (!cleanName) return ""
  const match = (Array.isArray(treatments) ? treatments : []).find((item) => normalizeText(item?.name) === cleanName)
  return match?.id || ""
}

function normalizeText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
}

function toDateInputValue(value) {
  if (!value) return ""
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
}

function getDurationMinutes(startValue, endValue, selectedTreatment) {
  const start = new Date(startValue)
  const end = new Date(endValue)
  if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && end > start) {
    return Math.max(15, Math.round((end.getTime() - start.getTime()) / 60000))
  }
  return Number(selectedTreatment?.durationMinutes || 30)
}

function durationOptions(selectedTreatment) {
  const set = new Set(DEFAULT_DURATION_OPTIONS)
  if (selectedTreatment?.durationMinutes) set.add(Number(selectedTreatment.durationMinutes))
  return Array.from(set).sort((a, b) => a - b)
}


function buildCurrentSlot(appointment) {
  const startTime = getAppointmentStart(appointment)
  const endTime = getAppointmentEnd(appointment)
  if (!startTime || !endTime) return null
  return {
    startTime,
    endTime,
    label: formatTimeFromValue(startTime),
    status: "available",
    reason: null,
    doctorId: getAppointmentDoctorId(appointment) || null,
    doctorName: appointment?.doctorName || appointment?.extendedProps?.doctorName || appointment?.doctor?.name || null,
    chairId: getAppointmentChairId(appointment) || null,
    isCurrent: true,
    date: toDateInputValue(startTime),
  }
}

function sameSlotValue(a, b) {
  if (!a || !b) return false
  const dateA = new Date(a)
  const dateB = new Date(b)
  if (!Number.isNaN(dateA.getTime()) && !Number.isNaN(dateB.getTime())) {
    return dateA.getTime() === dateB.getTime()
  }
  return String(a).slice(0, 16) === String(b).slice(0, 16)
}

function addMinutesToIso(value, minutes) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  date.setMinutes(date.getMinutes() + Number(minutes || 30))
  return date.toISOString()
}

function buildSlotRangeFromLocalLabel({ date, slot, durationMinutes }) {
  const fallbackStart = slot?.startTime || ""
  const fallbackEnd = slot?.endTime || addMinutesToIso(fallbackStart, Number(durationMinutes || 30))

  const cleanDate = String(date || "").slice(0, 10)
  const parsedLabel = parseSlotLabelTime(slot?.label)

  if (!/^\d{4}-\d{2}-\d{2}$/.test(cleanDate) || !parsedLabel) {
    return {
      startTime: fallbackStart,
      endTime: fallbackEnd,
    }
  }

  const [year, month, day] = cleanDate.split("-").map(Number)
  const hour = parsedLabel.hour
  const minute = parsedLabel.minute

  if (![year, month, day, hour, minute].every(Number.isFinite)) {
    return {
      startTime: fallbackStart,
      endTime: fallbackEnd,
    }
  }

  const localStart = new Date(year, month - 1, day, hour, minute, 0, 0)

  if (Number.isNaN(localStart.getTime())) {
    return {
      startTime: fallbackStart,
      endTime: fallbackEnd,
    }
  }

  const localEnd = new Date(localStart)
  localEnd.setMinutes(localEnd.getMinutes() + Number(durationMinutes || 30))

  return {
    startTime: localStart.toISOString(),
    endTime: localEnd.toISOString(),
  }
}

function formatTimeFromValue(value) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    const raw = String(value || "")
    const match = raw.match(/(\d{1,2}):(\d{2})/)
    if (match) return formatHour12FromParts(Number(match[1]), Number(match[2]))
    return raw.slice(11, 16)
  }

  return date.toLocaleTimeString("es-MX", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })
}

function formatSlotLabel(slot) {
  return formatTime12FromSlot(slot)
}

function parseSlotLabelTime(value) {
  const raw = String(value || "").trim().toLowerCase()

  const match = raw.match(/^(\d{1,2}):(\d{2})/)
  if (!match) return null

  let hour = Number(match[1])
  const minute = Number(match[2])

  if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
    return null
  }

  const isPm = /p\.?\s*m\.?/.test(raw) || /pm/.test(raw)
  const isAm = /a\.?\s*m\.?/.test(raw) || /am/.test(raw)

  if (isPm && hour < 12) {
    hour += 12
  }

  if (isAm && hour === 12) {
    hour = 0
  }

  return { hour, minute }
}

function formatTime12FromSlot(slot) {
  const parsedLabel = parseSlotLabelTime(slot?.label)

  if (parsedLabel) {
    return formatHour12FromParts(parsedLabel.hour, parsedLabel.minute)
  }

  return formatTimeFromValue(slot?.startTime)
}

function formatHour12FromParts(hour24, minute) {
  const safeHour = Number(hour24)
  const safeMinute = Number(minute)

  if (!Number.isFinite(safeHour) || !Number.isFinite(safeMinute)) {
    return ""
  }

  const period = safeHour >= 12 ? "p. m." : "a. m."
  const hour12 = safeHour % 12 || 12

  return `${hour12}:${String(safeMinute).padStart(2, "0")} ${period}`
}

function normalizeScheduleWarning(value) {
  const text = String(value || "").trim()
  if (!text) return ""
  if (text.toLowerCase().includes("horario por defecto")) return ""
  return text
}

const overlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 1000002,
  background: "rgba(15, 23, 42, 0.38)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 12,
  backdropFilter: "blur(3px)",
}

const modalStyle = {
  background: "#ffffff",
  borderRadius: 22,
  border: "1px solid #e2e8f0",
  boxShadow: "0 24px 80px rgba(15,23,42,0.28)",
  padding: 18,
  overflowY: "auto",
}

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: 12,
  alignItems: "flex-start",
  marginBottom: 14,
}

const eyebrowStyle = {
  fontSize: 11,
  fontWeight: 900,
  color: "#2563eb",
  textTransform: "uppercase",
  letterSpacing: 0.4,
}

const titleStyle = {
  margin: "4px 0 0",
  fontSize: 23,
  lineHeight: 1.12,
  color: "#0f172a",
}

const subtitleStyle = {
  margin: "6px 0 0",
  color: "#64748b",
  fontSize: 13.5,
  lineHeight: 1.35,
}

const closeButtonStyle = {
  width: 38,
  height: 38,
  borderRadius: 14,
  border: "1px solid #cbd5e1",
  background: "#f8fafc",
  color: "#334155",
  fontSize: 22,
  fontWeight: 900,
  cursor: "pointer",
}

const topGridStyle = (isMobile) => ({
  display: "grid",
  gridTemplateColumns: isMobile ? "1fr" : "minmax(0, 1fr) 160px",
  gap: 10,
  marginBottom: 10,
})

const resourceGridStyle = (isMobile) => ({
  display: "grid",
  gridTemplateColumns: isMobile ? "1fr" : "minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)",
  gap: 10,
  marginBottom: 12,
})

const fieldStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 5,
}

const labelStyle = {
  fontSize: 12,
  color: "#64748b",
  fontWeight: 900,
}


const inputStyle = {
  width: "100%",
  borderRadius: 12,
  border: "1px solid #cbd5e1",
  background: "#ffffff",
  padding: "10px 12px",
  fontSize: 15,
  color: "#0f172a",
  boxSizing: "border-box",
}

const fixedValueStyle = {
  ...inputStyle,
  background: "#f8fafc",
  fontWeight: 800,
}

const slotsPanelStyle = {
  marginTop: 14,
  padding: 12,
  borderRadius: 16,
  border: "1px solid #bfdbfe",
  background: "#eff6ff",
}

const slotsHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: 10,
  alignItems: "center",
  flexWrap: "wrap",
  marginBottom: 10,
}

const pillStyle = {
  borderRadius: 999,
  padding: "7px 10px",
  border: "1px solid #bfdbfe",
  background: "#ffffff",
  color: "#1d4ed8",
  fontSize: 12,
  fontWeight: 900,
}

const infoCardStyle = {
  padding: 11,
  borderRadius: 13,
  background: "#ffffff",
  border: "1px solid #dbeafe",
  color: "#1e3a8a",
  fontSize: 13,
  fontWeight: 800,
}

const dangerCardStyle = {
  padding: 11,
  borderRadius: 13,
  background: "#fef2f2",
  border: "1px solid #fecaca",
  color: "#991b1b",
  fontSize: 13,
  fontWeight: 800,
}

const softWarningStyle = {
  padding: 10,
  borderRadius: 13,
  background: "#fff7ed",
  border: "1px solid #fed7aa",
  color: "#9a3412",
  fontSize: 12.5,
  fontWeight: 800,
  marginBottom: 10,
}

const chipsRowStyle = {
  display: "flex",
  gap: 8,
  flexWrap: "wrap",
  marginBottom: 10,
}

const chipStyle = {
  borderRadius: 999,
  border: "1px solid #bfdbfe",
  background: "#ffffff",
  color: "#1d4ed8",
  padding: "9px 13px",
  fontWeight: 900,
  cursor: "pointer",
}

const selectedChipStyle = {
  background: "#2563eb",
  color: "#ffffff",
  border: "1px solid #2563eb",
}

const footerStyle = {
  marginTop: 16,
  display: "flex",
  gap: 10,
  justifyContent: "flex-end",
  flexWrap: "wrap",
}

const disabledButtonStyle = {
  ...ui.primaryButton,
  opacity: 0.55,
  cursor: "not-allowed",
}


